import type { ComponentChildren } from 'preact';
import { signal } from '@preact/signals';
import { Dialog } from './Dialog';
import { Button, type ButtonVariant } from '../primitives/Button';

interface ConfirmRequest {
  id: number;
  title: string;
  description?: ComponentChildren;
  confirmLabel?: string;
  cancelLabel?: string;
  confirmVariant?: ButtonVariant;
  resolve: (confirmed: boolean) => void;
}

const queue = signal<ConfirmRequest[]>([]);
let nextId = 1;

export function confirm(opts: {
  title: string;
  description?: ComponentChildren;
  confirmLabel?: string;
  cancelLabel?: string;
  confirmVariant?: ButtonVariant;
}): Promise<boolean> {
  return new Promise<boolean>((resolve) => {
    queue.value = [...queue.value, { id: nextId++, ...opts, resolve }];
  });
}

function answer(id: number, confirmed: boolean) {
  const req = queue.value.find((r) => r.id === id);
  if (!req) return;
  req.resolve(confirmed);
  queue.value = queue.value.filter((r) => r.id !== id);
}

/**
 * Mount this once near the root (e.g. in App.tsx) so any caller of `confirm()` gets a dialog.
 */
export function ConfirmHost() {
  const current = queue.value[0];
  return (
    <Dialog
      open={!!current}
      onClose={() => current && answer(current.id, false)}
      title={current?.title ?? ''}
      description={current?.description}
      size="sm"
      hideCloseButton
      footer={
        current ? (
          <>
            <Button variant="ghost" onClick={() => answer(current.id, false)}>
              {current.cancelLabel ?? 'Cancelar'}
            </Button>
            <Button
              variant={current.confirmVariant ?? 'primary'}
              onClick={() => answer(current.id, true)}
              autofocus
            >
              {current.confirmLabel ?? 'Confirmar'}
            </Button>
          </>
        ) : null
      }
    >
      {/* description renders in header; body intentionally empty */}
    </Dialog>
  );
}
